import { createApi } from '@reduxjs/toolkit/query/react';
import { buildTradeHistoryParams, TradeHistoryQueryArgs } from './tradeHistoryParams';
import { baseQuery } from '../baseQuery';
import {
  PublicUserProfile,
  UserTradeStats,
  ProfileTradeHistoryResponse,
  ProfileTradeMapResponse,
  ProfileSettings,
} from 'types/interfaces/profile';
import { UserHoverCardData } from 'types/interfaces/hoverCard';
import { SocialLinksResponse, SaveSocialLinksRequest } from 'types/interfaces/socialLinks';

export const profileApi = createApi({
  reducerPath: 'profileApi',
  baseQuery: baseQuery,
  tagTypes: ['PublicProfile', 'ProfileStats', 'ProfileTradeHistory', 'ProfileTradeMap', 'ProfileSettings', 'HoverCard', 'SocialLinks'],
  endpoints: (builder) => ({
    // Get public profile by username
    getPublicProfile: builder.query<PublicUserProfile, string>({
      query: (username) => ({
        url: `/public/profile/${encodeURIComponent(username)}`,
        method: 'GET',
        requiresAuth: false,
      }),
      providesTags: (_result, _error, username) => [
        { type: 'PublicProfile', id: username },
      ],
    }),

    // Get user's trade stats
    getUserTradeStats: builder.query<UserTradeStats, string>({
      query: (username) => ({
        url: `/public/profile/${encodeURIComponent(username)}/stats`,
        method: 'GET',
        requiresAuth: false,
      }),
      providesTags: (_result, _error, username) => [
        { type: 'ProfileStats', id: username },
      ],
    }),

    // Get user's trade history (paginated)
    getProfileTradeHistory: builder.query<
      ProfileTradeHistoryResponse,
      TradeHistoryQueryArgs
    >({
      query: ({ username, ...rest }) => {
        const params = buildTradeHistoryParams(rest);
        return {
          url: `/public/profile/${encodeURIComponent(username)}/trade-history?${params.toString()}`,
          method: 'GET',
          requiresAuth: false,
        };
      },
      providesTags: (_result, _error, { username }) => [
        { type: 'ProfileTradeHistory', id: username },
      ],
    }),

    // Trade-map aggregate for the profile map, the stats tiles and the share
    // card (self). ONE source for every number on every surface, so the sidebar
    // Trade Partners, the tile and the unfurl title agree by construction.
    getProfileTradeMap: builder.query<ProfileTradeMapResponse, string>({
      query: (username) => ({
        url: `/public/profile/${encodeURIComponent(username)}/trade-map`,
        method: 'GET',
        requiresAuth: false,
      }),
      providesTags: (_result, _error, username) => [
        { type: 'ProfileTradeMap', id: username },
      ],
    }),

    // Upload the rendered trade-map PNG for the crawler unfurl. The caller reads
    // nothing back but the status: 400 (type, size, signature), 429 (inside 10 s
    // of the previous upload) and network failures are all silent no-ops.
    uploadTradeMapImage: builder.mutation<void, FormData>({
      query: (formData) => ({
        url: '/profile/trade-map-image',
        method: 'POST',
        body: formData,
        requiresAuth: true,
        // No Content-Type: the browser sets multipart/form-data with its boundary
      }),
    }),

    // Get current user's profile settings (authenticated)
    getProfileSettings: builder.query<ProfileSettings, void>({
      query: () => ({
        url: '/settings/profile',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['ProfileSettings'],
    }),

    // Update current user's profile settings (authenticated)
    updateProfileSettings: builder.mutation<ProfileSettings, Partial<ProfileSettings>>({
      query: (body) => ({
        url: '/settings/profile',
        method: 'PUT',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['ProfileSettings'],
    }),

    // Check slug availability (public, rate-limited)
    checkSlugAvailability: builder.query<{ available: boolean }, string>({
      query: (slug) => ({
        url: `/slug/check/${encodeURIComponent(slug)}`,
        method: 'GET',
        requiresAuth: false,
      }),
    }),

    // Resolve custom slug to username (public)
    resolveSlug: builder.query<{ username: string }, string>({
      query: (slug) => ({
        url: `/slug/resolve/${encodeURIComponent(slug)}`,
        method: 'GET',
        requiresAuth: false,
      }),
    }),

    // Get user's social links (public)
    getUserSocialLinks: builder.query<SocialLinksResponse, string>({
      query: (username) => ({
        url: `/public/profile/${encodeURIComponent(username)}/social-links`,
        method: 'GET',
        requiresAuth: false,
      }),
      providesTags: (_result, _error, username) => [
        { type: 'SocialLinks', id: username },
      ],
    }),

    // Save social links (Elite only)
    saveSocialLinks: builder.mutation<SocialLinksResponse, SaveSocialLinksRequest>({
      query: (body) => ({
        url: '/settings/social-links',
        method: 'PUT',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['SocialLinks'],
    }),

    // Get lightweight hover card data for username preview
    getUserHoverCard: builder.query<UserHoverCardData, string>({
      query: (username) => ({
        url: `/users/${encodeURIComponent(username)}/hover-card`,
        method: 'GET',
        requiresAuth: false,
      }),
      keepUnusedDataFor: 60,
      providesTags: (_result, _error, username) => [
        { type: 'HoverCard', id: username },
      ],
    }),
  }),
});

export const {
  useGetPublicProfileQuery,
  useGetUserTradeStatsQuery,
  useGetProfileTradeHistoryQuery,
  useGetProfileTradeMapQuery,
  useUploadTradeMapImageMutation,
  useGetProfileSettingsQuery,
  useUpdateProfileSettingsMutation,
  useLazyCheckSlugAvailabilityQuery,
  useResolveSlugQuery,
  useLazyGetUserHoverCardQuery,
  useGetUserSocialLinksQuery,
  useSaveSocialLinksMutation,
} = profileApi;
